import { useEffect, useState } from "react";
import { SyncService } from "frontend/services/syncService";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { useAtom, useAtomValue } from "jotai";
import { layerRefAtom } from "state-atoms";
import {
  MyVotingStatus,
  useActiveVotingSession,
  Participation,
  votingAtom,
  checkParticipation,
  sumValuesOfObject,
} from "./voting-common";
import { getElementTypeForId } from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import { isElementVotable } from "elements/bridge";
import { getBoundingBox } from "utils/node-utils";
import { VotingSession } from "shared/datamodel/schemas/voting-session";
import style from "./voting-buttons.module.css";
import { useTimeout } from "react-use";
import { useBoardValue } from "frontend/state/board-state";

/**
 * This file defines the voting buttons that appear on the canvas when a user is participating in a voting session.
 * The buttons are attached to 'votable' elements and allow the user to vote 'yes' or 'no' on the element.
 * The buttons are only visible when the user is participating in a voting session.
 * The buttons are rendered as SVGs and are positioned relative to the element they are attached to.
 * The 'votable' property is defined on the main canvas-element Group, and the buttons appear on the lower-right corner
 * of each element.
 *
 * The button-click handler updates local state (the voting atom) and also the state on replicache.
 * That means voting status can be shown in real-time, we just don't do it right now.
 */

/**
 * Main component: just checks if user is in an active voting session to return the next component.
 */
export function VotingButtons({
  syncService,
  elementsIds,
  movingOffsetRef,
}: {
  syncService: SyncService<RW> | null;
  elementsIds: string[];
  movingOffsetRef?: React.MutableRefObject<{
    buttonRefs: Map<string, { element: HTMLDivElement; currentX: number; currentY: number }>;
  }>;
}) {
  const active = useActiveVotingSession(syncService);
  const [state, dispatch] = useAtom(votingAtom);
  const participation = checkParticipation(state, active);
  const isParticipating = active && participation == Participation.Joined;
  const timeLeftInSession = active ? active.start + active.duration * 1000 - Date.now() : -1;
  const refreshTime = isParticipating && timeLeftInSession > 0 ? timeLeftInSession : undefined;
  const [board] = useBoardValue(); //todo: is user and userAtomValue the same ??

  useTimeout(refreshTime); // this will refresh the component when voting ends, making the buttons disappear

  useEffect(() => {
    if (!isParticipating || timeLeftInSession <= 0) {
      return;
    }
    if (state?.votes && board.user) {
      syncService?.mutate.setVotingRecord({
        sessionId: state.sessionId,
        userId: board.user?.id || "",
        votes: state.votes,
      });
    } else {
      console.error("no votes or user id");
    }
  }, [state, board.user, isParticipating, timeLeftInSession]);

  function handleVote(elementId: string, vote: 1 | -1) {
    dispatch({
      type: "vote",
      elementId,
      vote,
      maxVotesPerUser: active!.numVotes,
      maxVotesOnObject: active!.maxVotesOnObject,
    });
  }

  if (!isParticipating || timeLeftInSession <= 0) return null;

  return (
    <VotingButtonsOnAllElements
      elementsIds={elementsIds}
      session={active}
      onVote={handleVote}
      userVotingState={state!}
      movingOffsetRef={movingOffsetRef}
    />
  );
}

/**
 * Create buttons for all elements that are votable.
 */
function VotingButtonsOnAllElements({
  elementsIds,
  session,
  onVote,
  userVotingState,
  movingOffsetRef,
}: {
  elementsIds: string[];
  session: VotingSession;
  onVote: (elementId: string, vote: 1 | -1) => void;
  userVotingState: MyVotingStatus;
  movingOffsetRef?: React.MutableRefObject<{
    buttonRefs: Map<string, { element: HTMLDivElement; currentX: number; currentY: number }>;
  }>;
}) {
  const layerRef = useAtomValue(layerRefAtom);
  const votesUsed = sumValuesOfObject(userVotingState.votes);
  const left = Math.max(0, session.numVotes - votesUsed);
  return (
    <>
      {elementsIds.map((id) => {
        const type = getElementTypeForId(id);
        if (isElementVotable(type)) {
          let votesOnObject = 0,
            votesLeftOnObject = session.maxVotesOnObject;
          if (userVotingState.votes[id]) {
            votesOnObject = userVotingState.votes[id] || 0;
            votesLeftOnObject = Math.max(0, session.maxVotesOnObject - votesOnObject);
          }
          return (
            <VotingButtonsForElement
              key={id}
              id={id}
              layerRef={layerRef}
              votesCast={votesOnObject}
              userCanStillVote={left > 0 && votesLeftOnObject > 0}
              userVotes={votesOnObject}
              onYes={(id) => onVote(id, 1)}
              onNo={(id) => onVote(id, -1)}
              movingOffsetRef={movingOffsetRef}
            />
          );
        } else {
          return null;
        }
      })}
    </>
  );
}

/**
 * Put voting buttons on a single element.
 *
 * - decide where to put the buttons (lower-right corner of the element)
 * - decide if should show "vote +1", "vote -1" or both
 */
function VotingButtonsForElement({
  id,
  layerRef,
  votesCast,
  userCanStillVote,
  userVotes,
  onYes,
  onNo,
  movingOffsetRef,
}: {
  id: string;
  layerRef: any;
  votesCast: number;
  userCanStillVote: boolean;
  userVotes: number;
  onYes: (id: string, thumbnail: any) => void;
  onNo: (id: string) => void;
  movingOffsetRef?: React.MutableRefObject<{
    buttonRefs: Map<string, { element: HTMLDivElement; currentX: number; currentY: number }>;
  }>;
}) {
  const findNode = () => layerRef.current.findOne("." + id);
  const [node, setNode] = useState(findNode);
  if (!node) {
    // when creating an element (or undoing delete) the element's id is in the list
    // and a voting-buttons component is rendered. However the node is not yet created.
    // so if that's the case we try to find the node again in a few ms.
    window.setTimeout(() => setNode(findNode() ?? "not-found"), 50);
    return null;
  }
  if (node == "not-found") {
    return null;
  }
  if (node.attrs.isDerivedData) {
    return null;
  }

  const uniqueId = node.id();
  const element = node.attrs.element;
  const getThumbnail = () => node.toDataURL();

  const elementType = getElementTypeForId(uniqueId);
  const boundingBox = getBoundingBox(elementType, element);
  if (!boundingBox) {
    // should never get here (only connectors might not have bounding-box, and
    // we don't put voting buttons on them)
    return null;
  }

  const x = boundingBox.x + boundingBox.width;
  const y = boundingBox.y + boundingBox.height;

  return (
    <ActualVoteButtons
      key={"vote buttons" + uniqueId}
      elementId={uniqueId}
      x={x}
      y={y}
      votesOnObject={votesCast}
      userCanStillVote={userCanStillVote}
      myVotesOnObject={userVotes}
      onVoteYes={() => onYes(uniqueId, getThumbnail())}
      onVoteNo={() => onNo(uniqueId)}
      movingOffsetRef={movingOffsetRef}
    />
  );
}

/**
 * The actual html element for the buttons
 */
function ActualVoteButtons({
  elementId,
  x,
  y,
  votesOnObject,
  userCanStillVote,
  myVotesOnObject,
  onVoteYes,
  onVoteNo,
  movingOffsetRef,
}: {
  elementId: string;
  x: number;
  y: number;
  votesOnObject: number;
  userCanStillVote: boolean;
  myVotesOnObject: number;
  onVoteYes: () => void;
  onVoteNo: () => void;
  movingOffsetRef?: React.MutableRefObject<{
    buttonRefs: Map<string, { element: HTMLDivElement; currentX: number; currentY: number }>;
  }>;
}) {
  // Register/unregister this button's DOM element with its current position
  const refCallback = (element: HTMLDivElement | null) => {
    if (movingOffsetRef) {
      if (element) {
        movingOffsetRef.current.buttonRefs.set(elementId, {
          element,
          currentX: x,
          currentY: y,
        });
      } else {
        movingOffsetRef.current.buttonRefs.delete(elementId);
      }
    }
  };

  return (
    <div
      ref={refCallback}
      role="group"
      className={style.buttonGroup}
      style={{
        position: "absolute",
        transform: `translate(${x}px, ${y}px) translateX(-50%) translateY(-50%)`,
        overflow: "visible",
      }}
    >
      <div
        role="button"
        data-testid="vote-yes"
        onClick={onVoteYes}
        aria-disabled={!userCanStillVote}
        data-disabled={!userCanStillVote}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.30005 7.5H13.7 M7.5 1.2998V13.6998"
            stroke="white"
            strokeWidth="2.325"
            strokeLinecap="square"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      <div data-disabled={votesOnObject == 0}>{votesOnObject > 0 ? votesOnObject : ""}</div>

      <div
        role="button"
        data-testid="vote-no"
        className={myVotesOnObject == 0 ? style.inactive : undefined}
        onClick={onVoteNo}
        aria-disabled={myVotesOnObject == 0}
        data-disabled={myVotesOnObject == 0}
      >
        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M1.30005 7.5H13.7"
            stroke="white"
            strokeWidth="2.325"
            strokeLinecap="square"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}
